import dynamic from 'next/dynamic';

import React, { useState, Fragment, useEffect } from "react";
import Modal from "@/components/ui/Modal";
import { Icon } from "@iconify/react/dist/iconify.js";
const Button = dynamic(() => import('@/components/ui/Button'));
import {
  uploadReportScreenshots,
  addUserReport,
  updateUserReport,
  getGames,
} from "@/utils/firebaseFunctions";
import ShortUniqueId from "short-unique-id";
import MessageModal from "@/components/ui/Message-Modal";

function ReportModal({ isOpen, onClose, user, report }) {
  const [uid, setUid] = useState("");
  const [walletAddress, setWalletAddress] = useState("");
  const [currentDateTime, setCurrentDateTime] = useState("");
  const [status, setStatus] = useState("OPEN");
  const [issueArea, setIssueArea] = useState("LOYALTY");
  const [gameName, setGameName] = useState(""); // will be used for game_name [if issue area is game
  const [severity, setSeverity] = useState("");
  const [dateOfIssue, setDateOfIssue] = useState("");
  const [description, setDescription] = useState("");
  const [round, setRound] = useState("")
  const [buyFrom, setBuyFrom] = useState("")
  const [purchaseLocation, setPurchaseLocation] = useState("")

  const [screenshots, setScreenshots] = useState([]); // will be used for multiple screenshots
  const [showFormErrors, setShowFormErrors] = useState(false);
  const reportId = new ShortUniqueId({ length: 12 });

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [activeError, setActiveError] = useState(null);
  const [activeErrorTitle, setActiveErrorTitle] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [games, setGames] = useState([]);
  const [gamesLoading, setGamesLoading] = useState(true);

  useEffect(() => {
    getGamesData();

    if (user) {
      setUid(user.user_id || user.uid);
      setWalletAddress(user.wallet);
    }

    if (report) {
      setIssueArea(report.issue_area);
      setSeverity(report.severity);
      setDateOfIssue(report.date_time_issue);
      setDescription(report.description);
      setScreenshots(report.screenshots); // will be used for multiple screenshots
      setStatus(report.status);
      setCurrentDateTime(report.datetime_current);
      setGameName(report.game_name);
    } else {
      setCurrentDateTime(
        new Date().toLocaleDateString("en-US", {
          timeZone: "UTC",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        })
      );
    }

    return () => {
      //console.log("ReportModal unmounted");
    };
  }, []);

  const getGamesData = async () => {
    try {
      console.log("Fetching games data...");
      const gamesData = await getGames();
      // console.log("Games data received:", gamesData);
      if (Array.isArray(gamesData) && gamesData.length === 0) {
        console.warn("Games data is an empty array");
      }
      setGames(gamesData);
      setGamesLoading(false);
    } catch (error) {
      console.error("Error fetching games:", error);
      setGamesLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (
      !issueArea ||
      !severity ||
      !dateOfIssue ||
      !description ||
      (issueArea === "GAME" && !gameName)
    ) {
      setShowFormErrors(true);
      return;
    }

    setIsSubmitting(true);

    let DOI = new Date(dateOfIssue).getTime();
    let CDT = new Date(currentDateTime).getTime();

    if (DOI > CDT) {
      setActiveError("Date of Issue cannot be in the future.");
      setActiveErrorTitle("Error");
      setShowErrorModal(true);
      setIsSubmitting(false);
      return;
    }

    let reportData = {
      report_id: report ? report.report_id : reportId.rnd(),
      uid: uid,
      wallet: walletAddress,
      datetime_current: CDT,
      status: status,
      issue_area: issueArea,
      severity: severity,
      date_time_issue: DOI,
      description: description,
      screenshots: screenshots || [],
      admin_is_rewarded: report ? report.admin_is_rewarded : false,
      admin_points_awarded: report ? report.admin_points_awarded : 0,
      updated_at: report
        ? parseInt((new Date().getTime() / 1000).toFixed(0))
        : null,
      game_name: gameName || null,
      round: round || null, // Add round to reportData
      buyFrom: buyFrom || null, // Add buyFrom to reportData
      purchase_location: purchaseLocation || null, // Add purchaseLocation to reportData
    };

    if (screenshots.length > 0) {
      uploadReportScreenshots(reportData.report_id, screenshots)
        .then((screenshotUrls) => {
          reportData.screenshots = screenshotUrls;

          // Add report to database
          console.log(reportData);

          let addProcess = false;
          let updateProcess = false;

          if (report) {
            console.log("Original Report Data: ", report);
            console.log("Updated Report Data: ", reportData);
            updateProcess = updateUserReport(reportData.report_id, reportData);
          } else {
            addProcess = addUserReport(reportData);
          }

          if (addProcess || updateProcess) {
            onClose(false);
          } else {
            onClose(false);
          }
        })
        .catch((error) => {
          setActiveError(error.message);
          setActiveErrorTitle("Error");
          setShowErrorModal(true);
          setIsSubmitting(false);
          return;
        });
    } else {
      let addProcess = false;
      let updateProcess = false;

      if (report) {
        console.log("Original Report Data: ", report);
        console.log("Updated Report Data: ", reportData);
        updateProcess = updateUserReport(reportData.report_id, reportData);
      } else {
        addProcess = addUserReport(reportData);
      }

      if (addProcess || updateProcess) {
        onClose(true);
      } else {
        onClose(false);
      }
    }
  };

  const handleScreenshotChange = (e) => {
    if (e.target.files.length > 0) {
      let newFiles = [];
      for (let i = 0; i < e.target.files.length; i++) {
        newFiles.push(e.target.files[i]);
      }

      setScreenshots((prevScreenshots) => {
        const totalScreenshots = [...prevScreenshots, ...newFiles];

        // Check the total length of the screenshots array
        if (totalScreenshots.length > 5) {
          // Show an error message if the total length is more than 5
          alert(
            "You couldn't upload more than 5 screenshots. Please consider to delete some of them (if already exists before) or please select maximum 5."
          );

          // Clear the input but keep the string ones if they exist before
          e.target.value = null;
          return prevScreenshots.filter(
            (screenshot) => typeof screenshot === "string"
          );
        }

        return totalScreenshots;
      });

      console.log("Screenshots: ", newFiles);
    }
  };
  const handleDateChange = (date) => {
    setDateOfIssue(date.target.value);
  };

  return (
    <Fragment>
      <Modal
        activeModal={isOpen}
        onClose={onClose}
        centered={true}
        title={report ? "Update Report" : "Submit Report"}
        bodyClass={"!py-4 !px-0"}
        themeClass="bg-metaxseed-blue !px-6 !py-3"
        className="max-w-xl bg-metaxseed-dark-gray report-modal"
      >
        <div className="flex flex-col items-stretch justify-stretch flex-1">
          <div className=" px-6 flex flex-col items-stretch justify-stretch  overflow-y-auto max-h-[70vh]">
            {!report && (
              <Fragment>
                <div className="flex-1 flex flex-row items-stretch justify-stretch relative mb-2">
                  <input
                    type="text"
                    placeholder="UID"
                    className="font-normal gothamBook border text-xs rounded block w-full px-2.5 py-2 bg-zinc-700 border-zinc-600 placeholder-zinc-400 text-zinc-400 opacity-100 !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none"
                    disabled
                    value={uid}
                  />
                  <div className="text-xs min-w-24 absolute right-[1px] top-1/2 transform -translate-y-1/2 gothamMedium pl-4 pr-2 bg-zinc-700 text-zinc-400 custom-placeholder-report z-10 cursor-default">
                    <Icon icon="mdi:account" className="text-xs mr-2" />
                    <span>UID</span>
                  </div>
                </div>
                <div className="flex-1 flex flex-row items-stretch justify-stretch relative mb-2">
                  <input
                    type="text"
                    placeholder="Wallet Address"
                    className="font-normal gothamBook border text-xs rounded block w-full px-2.5 py-2 bg-zinc-700 border-zinc-600 placeholder-zinc-400 text-zinc-400 opacity-100 !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none"
                    disabled
                    value={walletAddress}
                  />
                  <div className="text-xs min-w-24 absolute right-[1px] top-1/2 transform -translate-y-1/2 gothamMedium pl-4 pr-2 bg-zinc-700 text-zinc-400 custom-placeholder-report z-10 cursor-default">
                    <Icon icon="mdi:wallet" className="text-xs mr-2" />
                    <span>Wallet</span>
                  </div>
                </div>
                <div className="flex-1 flex flex-row items-stretch justify-stretch relative mb-2">
                  <input
                    type="text"
                    placeholder="Current Date/Time"
                    className="font-normal gothamBook border text-xs rounded block w-full px-2.5 py-2 bg-zinc-700 border-zinc-600 placeholder-zinc-400 text-zinc-400 opacity-100 !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none"
                    disabled
                    value={currentDateTime}
                  />
                  <div className="text-xs min-w-24 absolute right-[1px] top-1/2 transform -translate-y-1/2 gothamMedium pl-4 pr-2 bg-zinc-700 text-zinc-400 custom-placeholder-report z-10 cursor-default">
                    <Icon
                      icon="mdi:clock-time-four-outline"
                      className="text-xs mr-2"
                    />
                    <span>Time</span>
                  </div>
                </div>

                <div className="flex-1 flex flex-row items-stretch justify-stretch relative">
                  <select
                    className="font-normal gothamBook border text-xs rounded block w-full px-2.5 py-2 bg-zinc-700 border-zinc-600 placeholder-zinc-400 text-zinc-400 appearance-none opacity-100 !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none"
                    disabled
                    value={status}
                  >
                    <option value="OPEN">OPEN</option>
                    <option value="CLOSED">CLOSED</option>
                    <option value="PENDING">PENDING</option>
                    <option value="REJECTED">REJECTED</option>
                  </select>
                  <div className="text-xs min-w-24 absolute right-[1px] top-1/2 transform -translate-y-1/2 gothamMedium pl-4 pr-2 bg-zinc-700 text-zinc-400 custom-placeholder-report z-10 cursor-default">
                    <Icon icon="el:question-sign" className="text-xs mr-2" />
                    <span>Status</span>
                  </div>
                </div>

                <div className="flex-1 flex flex-row items-stretch justify-stretch relative mb-4 mt-6">
                  <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent to-zinc-700"></div>
                  <div className="h-[1px] flex-1 bg-gradient-to-r from-zinc-700 to-transparent"></div>
                </div>
              </Fragment>
            )}

            {report && (
              <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
                <label
                  className="block text-sm gothamBook mb-1"
                  htmlFor="status"
                >
                  Status:
                </label>
                <select
                  className={
                    "font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700  placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none" +
                    (showFormErrors && !status
                      ? " border-red-500"
                      : " border-zinc-600")
                  }
                  value={status}
                  required
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option disabled value="" style={{ color: "#a0aec0" }}>
                    Choose one
                  </option>
                  {status && <option value={status}>{status}</option>}
                  <option value="USER_REOPENED">USER_REOPENED</option>
                  <option value="USER_SOLVED">USER_SOLVED</option>
                </select>
                {showFormErrors && !status && (
                  <small className="text-xs text-red-500 gothamBook">
                    <span className="text-red-500">*</span> Required
                  </small>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">

            <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
              <label
                className="block text-sm gothamBook mb-1"
                htmlFor="issueArea"
              >
                Issue Area:
              </label>
              <select
                className={
                  "font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700  placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none" +
                  (showFormErrors && !issueArea
                    ? " border-red-500"
                    : " border-zinc-600")
                }
                value={issueArea}
                disabled={report ? true : false}
                required
                onChange={(e) => setIssueArea(e.target.value)}
              >
                <option disabled value="" style={{ color: "#a0aec0" }}>
                  Choose one
                </option>
                <option value="LOYALTY">LOYALTY</option>
                <option value="SEASONS">SEASONS</option>
                <option value="WEBSITE">WEBSITE</option>
                <option value="WALLET">WALLET</option>
                <option value="PERFORMANCE">PERFORMANCE</option>
                <option value="DATA">DATA</option>
                <option value="GAME">GAME</option>
                <option value="MISSIONS">MISSIONS</option>
                <option value="NODES">NODES</option>
                <option value="NODE REWARDS">NODE REWARDS</option>
                <option value="NODE HOSTING">NODE HOSTING</option>
                <option value="NFT">NFT</option>
                <option value="TOKEN STAKE">TOKEN STAKE</option>
                <option value="TOKEN AIRDROP">TOKEN AIRDROP</option>
                <option value="TOKEN VESTING">TOKEN VESTING</option>
                <option value="OTHER">OTHER</option>
              </select>
              {showFormErrors && !issueArea && (
                <small className="text-xs text-red-500 gothamBook">
                  <span className="text-red-500">*</span> Required
                </small>
              )}
            </div>
            <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
              <label
                className="block text-sm gothamBook mb-1"
                htmlFor="severity"
              >
                Severity:
              </label>
              <select
                className={
                  "font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700 placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none" +
                  (showFormErrors && !severity
                    ? " border-red-500"
                    : " border-zinc-600")
                }
                value={severity}
                disabled={report ? true : false}
                required
                onChange={(e) => setSeverity(e.target.value)}
              >
                <option disabled value="" style={{ color: "#a0aec0" }}>
                  Choose one
                </option>
                <option value="LOW">LOW</option>
                <option value="MEDIUM">MEDIUM</option>
                <option value="HIGH">HIGH</option>
                <option value="CRITICAL">CRITICAL</option>
              </select>
              {showFormErrors && !severity && (
                <small className="text-xs text-red-500 gothamBook">
                  <span className="text-red-500">*</span> Required
                </small>
              )}
            </div>
        {(issueArea === "TOKEN AIRDROP" || issueArea === "TOKEN VESTING") && (
  <Fragment>
    <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
      <label className="block text-sm gothamBook mb-1">
        Round: <span style={{ color: "red" }}>*</span>
      </label>
      <select
        className="font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700 placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none"
        value={round}
        required
        onChange={(e) => setRound(e.target.value)}
      >
        <option disabled value="" style={{ color: "#a0aec0" }}>
          Choose one
        </option>
        <option value="SEED">SEED</option>
        <option value="STRATEGIC">STRATEGIC</option>
        <option value="PRIVATE">PRIVATE</option>
        <option value="PUBLIC">PUBLIC</option>
      </select>
    </div>

    <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
      <label className="block text-sm gothamBook mb-1">
        Buy From: <span style={{ color: "red" }}>*</span>
      </label>
      <select
        className="font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700 placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none"
        value={buyFrom}
        required
        onChange={(e) => setBuyFrom(e.target.value)}
      >
        <option disabled value="" style={{ color: "#a0aec0" }}>
          Choose one
        </option>
        <option value="VC">VC</option>
        <option value="LAUNCHPAD">LAUNCHPAD</option>
        <option value="DIRECT">DIRECT</option>
      </select>
    </div>

    <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
      <label className="block text-sm gothamBook mb-1">
        Where did you buy the token? <span style={{ color: "red" }}>*</span>
      </label>
      <input
        type="text"
        className="font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700 placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none"
        value={purchaseLocation}
        required
        onChange={(e) => setPurchaseLocation(e.target.value)}
      />
    </div>
  </Fragment>
)}

        <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
              <label
                className="block text-sm gothamBook mb-1"
                htmlFor="dateOfIssue"
              >
                Date of Issue:
              </label>
              <input
                type="date"
                className={
                  "font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700 placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none" +
                  (showFormErrors && !dateOfIssue
                    ? " border-red-500"
                    : " border-zinc-600")
                }
                required
                disabled={report ? true : false}
                value={
                  // We need to check if the dateOfIssue is a unix or null value
                  dateOfIssue
                    ? new Date(dateOfIssue).toISOString().split("T")[0]
                    : ""
                }
                onChange={handleDateChange}
              />
              {showFormErrors && !dateOfIssue && (
                <small className="text-xs text-red-500 gothamBook">
                  <span className="text-red-500">*</span> Required
                </small>
              )}
            </div>

            <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
              {screenshots && screenshots.length > 0 && (
                <div className="flex-1 flex flex-row items-stretch justify-stretch relative mb-2">
                  Screenshots:{" "}
                  {screenshots.map((screenshot, index) => (
                    <a
                      href={screenshot}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-500 underline ml-2"
                      key={index}
                    >
                      View {index + 1}
                    </a>
                  ))}
                </div>
              )}
              <label
                className="block text-sm gothamBook mb-1"
                htmlFor="screenshots"
              >
                Upload New Screenshots:
              </label>
              <input
                type="file"
                multiple
                className={
                  "font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700 placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none"
                }
                required
                onChange={handleScreenshotChange}
              />
            </div>

            </div>


            

            {issueArea === "GAME" && !gamesLoading && (
              <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
                <label
                  className="block text-sm gothamBook mb-1"
                  htmlFor="game_name"
                >
                  Game:
                </label>
                <select
                  className={
                    "font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700  placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none" +
                    (showFormErrors && issueArea === "GAME" && !gameName
                      ? " border-red-500"
                      : " border-zinc-600")
                  }
                  value={gameName}
                  required
                  disabled={report ? true : false}
                  onChange={(e) => setGameName(e.target.value)}
                >
                  <option disabled value="" style={{ color: "#a0aec0" }}>
                    Choose one
                  </option>
                  {games.length > 0 ? (
                    games.map((game, index) => (
                      <option value={game.game_value} key={index}>
                        {game.game_name ? game.game_name.toUpperCase() : 'Unknown Game'}
                      </option>
                    ))
                  ) : (
                    <option disabled>No games available</option>
                  )}
                </select>
                {showFormErrors && issueArea === "GAME" && !gameName && (
                  <small className="text-xs text-red-500 gothamBook">
                    <span className="text-red-500">*</span> Required
                  </small>
                )}
              </div>
            )}

            

            

            <div className="flex-1 flex flex-col items-stretch justify-stretch relative mb-2">
              <label
                className="block text-sm gothamBook mb-1"
                htmlFor="description"
              >
                Description:
              </label>
              <textarea
                className={
                  "font-normal gothamBook border text-sm rounded block w-full p-2.5 bg-zinc-700  placeholder-zinc-400 text-white !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none" +
                  (showFormErrors && !description
                    ? " border-red-500"
                    : " border-zinc-600")
                }
                required
                disabled={report ? true : false}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              ></textarea>
              {showFormErrors && !description && (
                <small className="text-xs text-red-500 gothamBook">
                  <span className="text-red-500">*</span> Required
                </small>
              )}
            </div>

            
          </div>

          <div className=" px-6 flex flex-col items-stretch justify-stretch">
            <div className="flex-1 flex flex-row items-stretch justify-stretch relative mt-4">
              {isSubmitting && (
                <Button
                  className={
                    "font-normal w-full btn-secondary !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none z-10 "
                  }
                  isLoading
                />
              )}
              {!isSubmitting && (
                <Button
                  onClick={handleSubmit}
                  text="Submit"
                  className={
                    "font-normal w-full btn-primary !ring-0 !ring-transparent !ring-offset-0 outline-none hover:outline-none gothamMedium !shadow-none z-10 "
                  }
                />
              )}
            </div>
          </div>
        </div>
      </Modal>

      {showErrorModal && (
        <MessageModal
          title={activeErrorTitle || "Error"}
          status={false}
          centered
          activeModal={showErrorModal}
          className="max-w-xl errorModalShadow"
          onClose={(event) => {
            if (event) {
              setShowErrorModal(false);
              setTimeout(() => {
                setActiveError(null);
                setActiveErrorTitle(null);
              }, 300);
            }
          }}
        >
          <div className="font-medium py-2">
            {activeError || "An error occurred. Please try again later."}
          </div>
        </MessageModal>
      )}
    </Fragment>
  );
}

export default ReportModal;
